/**
 * Runtime feature flags, served by Reflag.
 *
 * Distinct from `config/featureFlags.ts`, which holds build-time constants you
 * flip and redeploy. These are evaluated per user at runtime, so a flag can be
 * rolled out (or pulled back) without shipping a build.
 *
 * The publishable key comes from `VITE_REFLAG_PUBLISHABLE_KEY`, injected by the
 * staging / production deploy workflows. Local dev and CI leave it unset, which
 * is deliberate: with no key the client can't evaluate anything and every flag
 * reads as disabled, so unflagged behaviour is what you get by default.
 */

import { ReflagProvider, useFlag } from "@reflag/react-sdk";
import type { ReactNode } from "react";

import { useAuthStore } from "@/store/authStore";

/**
 * Gates the generation-recovery work: SSE auto-reconnect, resuming a session
 * paused during planning, and the retry-planning status flip. See GOM-316.
 */
export const GENERATION_RECOVERY_FLAG = "app-generation-recoverable";

const PUBLISHABLE_KEY = import.meta.env.VITE_REFLAG_PUBLISHABLE_KEY ?? "";

/**
 * Wraps the app in the Reflag client.
 *
 * Mounted unconditionally — even with no publishable key — because `useFlag`
 * throws when no provider is above it. Rendering it always means a missing key
 * degrades to "every flag off" instead of crashing the tree.
 *
 * Children render immediately rather than waiting on the first flag fetch
 * (`initialLoading` defaults to false): flags gate incremental behaviour here,
 * not whole screens, so blocking the first paint on a network round-trip would
 * cost more than it buys.
 */
export function ReflagFlagsProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore(s => s.user);

  return (
    <ReflagProvider
      publishableKey={PUBLISHABLE_KEY}
      // Keyed on the user id so a sign-in / sign-out re-evaluates against the
      // new identity instead of reusing the previous user's flag set.
      key={user?.id ?? "anonymous"}
      context={
        user
          ? { user: { id: user.id, name: user.displayName, email: user.email } }
          : {}
      }
    >
      {children}
    </ReflagProvider>
  );
}

/**
 * Whether the generation-recovery behaviour is enabled for the current user.
 * Reads `false` whenever the flag is off, unknown, or the client has no key —
 * so every call site must treat `false` as "behave the way we did before".
 */
export function useGenerationRecoveryFlag(): boolean {
  return useFlag(GENERATION_RECOVERY_FLAG).isEnabled;
}
